import React, { useEffect, useMemo, useState } from "react";
import { useUser } from "../state";
import { api } from "../api";
import { RegisterOrLoginPrompt } from "../Auth/RegisterOrLoginPrompt";
import { CommentJSON } from "../models/comments";
import { ForumTopicJSON } from "../models/forum_topics";
import { RLink } from "../DesignSystem/RLink";
import { MentionTextarea } from "../DesignSystem/MentionTextarea";
import type { Entry } from "../models/entries";
import type { User } from "../models/users";
import { TestIds } from "../testIds";

export type LeaveACommentProps = {
  canPostAnonymously: boolean;
  entry?: Entry;
  user?: User;
  topic?: ForumTopicJSON;
  showSubscriptionBox?: boolean;
  onAddComment: (comment: CommentJSON) => void;
};

export type LeaveACommentHandle = {
  replyTo: (username: string | undefined) => void;
};

export const commentReplyHandle: LeaveACommentHandle = {
  replyTo: () => {},
};

const findPos = (element: HTMLElement | null): number => {
  let current = element;
  let offset = 0;
  while (current?.offsetParent) {
    offset += current.offsetTop;
    current = current.offsetParent as HTMLElement;
  }
  return offset;
};

export const LeaveAComment = ({
  canPostAnonymously,
  entry,
  user,
  topic,
  showSubscriptionBox,
  onAddComment,
}: LeaveACommentProps): JSX.Element => {
  const userState = useUser();
  const currentUser = userState.type === "logged-in" ? userState.user : undefined;
  const [commentText, setCommentText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [subscribe, setSubscribe] = useState(false);
  const [isAnon, setIsAnon] = useState(false);

  useEffect(() => {
    setIsAnon(canPostAnonymously);
  }, [canPostAnonymously]);

  useEffect(() => {
    commentReplyHandle.replyTo = replyTo;
    return () => {
      commentReplyHandle.replyTo = () => {};
    };
  });

  const prompts = useMemo(() => {
    if (entry) {
      return [
        "Say something clever.",
        "Say something intelligent.",
        "Drop some mad knowledge.",
        "Say something useful.",
        "Remember to be nice!",
        "Give some helpful advice.",
        "Post a me- wait NO, GIVE USEFUL ADVICE",
      ];
    }

    if (user) {
      return [
        "Say something nice!",
        "Say how much this particular fellow means to you!",
        "Say something useful.",
        "Remember to be nice!",
        "Reference an inside joke in an annoying way that no one else will understand.",
      ];
    }

    return [];
  }, [entry, user]);

  const replyTo = (username: string | undefined): void => {
    const mention = username ? `@${username}` : `@Anonymous`;
    setCommentText(`${mention} ${commentText}`);
    window.scroll(0, findPos(document.getElementById("leave-a-comment")));
  };

  const submit = async (): Promise<void> => {
    setSubmitting(true);

    let type: "entry" | "user" | "topic" | undefined;
    let associatedId: number | undefined;

    if (entry) {
      type = "entry";
      associatedId = entry.id;
    } else if (user) {
      type = "user";
      associatedId = user.id;
    } else if (topic) {
      type = "topic";
      associatedId = topic.id;
    }

    if (!type || !associatedId) {
      setSubmitting(false);
      return;
    }

    if (commentText.trim().length === 0 && currentUser) {
      alert(`That's an empty comment. Try actually writing something next time, ${currentUser.username}.`);
      setSubmitting(false);
      return;
    }

    // Convert @[username] markup to plain @username for storage
    const plainContent = commentText.replace(/@\[([^\]]+)\]/g, "@$1");

    const comment = await api.post<"/api/entry/:id/comment">(`/api/entry/${associatedId}/comment`, {
      content: plainContent,
      anonymous: isAnon,
      type,
      subscribe,
    });

    if (comment && !api.isToast(comment)) {
      setSubmitting(false);
      onAddComment(comment);
      setCommentText("");
    } else {
      alert(
        "Failed to post the comment. Check your internet connection, or contact johnfn if the problem persists."
      );
      setSubmitting(false);
    }
  };

  if (!currentUser) {
    return (
      <div>
        <h3>Leave a Comment</h3>

        <RegisterOrLoginPrompt doWhat="leave a comment" />
      </div>
    );
  }

  const placeholder = prompts.length
    ? prompts[Math.floor(Math.random() * prompts.length)]
    : "Leave a comment.";
  const avatarSrc = isAnon ? "/img/anonymous-chip.png" : currentUser.avatar_url;
  const displayName = isAnon ? "Anonymous Chip" : currentUser.username;
  const dateString = new Date().toDateString();

  return (
    <div
      id="leave-a-comment"
      className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800"
    >
      <div className="mb-3 flex items-start gap-3">
        <div className="flex-shrink-0">
          <img src={avatarSrc} className="h-12 w-12 rounded-lg object-cover" alt={displayName} />
        </div>
        <div className="flex-grow">
          <div className="text-lg font-semibold text-slate-800 dark:text-slate-100">
            {displayName}
          </div>
          <div className="text-sm text-slate-500 dark:text-slate-400">{dateString}</div>
        </div>
      </div>

      <div className="mb-3">
        <MentionTextarea
          value={commentText}
          onChange={setCommentText}
          placeholder={placeholder}
        />
      </div>

      <div className="flex flex-col gap-2 text-sm text-slate-600 dark:text-slate-300">
        <div>
          <button
            className="rounded bg-blue-600 px-4 py-2 text-white shadow hover:bg-blue-700 disabled:bg-slate-400"
            onClick={submit}
            disabled={submitting}
          >
            {submitting ? "Submitting..." : "Submit Comment"}
          </button>
        </div>
        {canPostAnonymously && (
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              data-testid={TestIds.PostAnonymouslyCheckbox}
              checked={isAnon}
              onChange={(event) => setIsAnon(event.currentTarget.checked)}
            />
            <label>
              <span>Post anonymously</span>
            </label>
          </div>
        )}
      </div>

      <div className="mt-3 text-sm text-slate-500 dark:text-slate-400">
        <div>
          @mentions supported.{" "}
          <RLink to="/registry">See all users.</RLink>
        </div>
        <div>
          *stars* make <em>italics</em>, **double stars** make{" "}
          <strong>bold</strong>.{" "}
          <RLink to="https://github.com/adam-p/markdown-here/wiki/Markdown-Cheatsheet">
            Markdown cheatsheet.
          </RLink>
        </div>
      </div>

      {showSubscriptionBox && (
        <div className="mt-2 flex items-center gap-2 text-sm text-slate-600">
          <i className="fa fa-envelope-o pr-[10px] text-[24px] text-[#d4d4d4]" aria-hidden="true"></i>
          <input
            type="checkbox"
            defaultChecked={subscribe}
            onChange={(event) => setSubscribe(event.currentTarget.checked)}
            className="h-4 w-4"
          />
          <label>
            <span>Subscribe to thread notifications</span>
          </label>
        </div>
      )}
    </div>
  );
};
